// Configuração do Supabase
export const SUPABASE_CONFIG = {
  url: import.meta.env.VITE_SUPABASE_URL || 'https://esakubufsfykhrxcbfwp.supabase.co',
  anonKey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImVzYWt1YnVmc2Z5a2hyeGNiZndwIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTIxNTc4OTIsImV4cCI6MjA2NzczMzg5Mn0.JPl2DmJzl-auvjzos1cIqlgTVjUE6fQeHe9i--eN3DM',
  serviceRoleKey: import.meta.env.SUPABASE_SERVICE_ROLE_KEY || '' // Geralmente não deve ser exposta no frontend, mas mantemos compatibilidade se estiver sendo usada
};

// Interface para os dados de precificação
export interface PrecificacaoData {
  /** Apenas fixação Solo: quando false, estrutura/fundação/equip. alugados = 0 nos cálculos. */
  incluir_custos_estrutura_fundacao: boolean;
  valor_estrutura_metallica: number;
  valor_fundacao: number;
  valor_equipamentos_alugados: number;
  custo_materiais: number;
  comissao_vendedor: number;
  comissao_parceiro: number;
  comissao_parceiro_pai: number;
  integracao_r_wp: number;
  impostos_percentual: number;
  mao_obra_por_placa: number;
}

// Interface para os resultados dos cálculos
export interface CalculosOutput {
  preco_projeto: number;
  preco_integracao: number;
  preco_kits: number;
  preco_estrutura_metallica: number;
  comissao_vendedor: number;
  comissao_parceiro: number;
  comissao_parceiro_pai: number;
  impostos: number;
  mao_obra: number;
  custo_total_implantacao: number;
  custo_total_integracao: number;
  preco_por_wp: number;
  lucro_liquido: number;
  lucro_liquido_percentual: number;
}

// Função específica para buscar lead por ID
export const fetchLeadData = async (leadId: string) => {
  // CORREÇÃO CRÍTICA: Obter token de autenticação do usuário
  const { supabase } = await import('@/integrations/supabase/client');
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  
  if (!token) {
    console.error('❌ fetchLeadData - Token de autenticação não encontrado');
    throw new Error('Token de autenticação necessário');
  }
  
  console.log('🔐 fetchLeadData - Token obtido:', token ? `${token.substring(0, 20)}...` : 'none');
  
  // CORREÇÃO: Buscar campos corretos da tabela leads
  // A coluna 'responsavel' não existe - usar 'vendedor_id' com JOIN em profiles para buscar o nome
  console.log('🔍 fetchLeadData - Buscando lead com ID:', leadId);
  
  const { data, error } = await supabase
    .from('leads')
    .select(`
      tipo_fixacao,
      tipo_telha,
      grupo_tarifario,
      nome,
      razao_social,
      representante_legal,
      tipo,
      parceiro_id,
      vendedor:profiles!leads_vendedor_id_fkey(nome),
      parceiro:profiles!leads_parceiro_id_fkey(parceiro_cadastrado_por)
    `)
    .eq('id', leadId)
    .single();
  
  if (error) {
    console.error('❌ fetchLeadData - Erro na requisição:', error);
    throw new Error(`Erro na requisição: ${error.message}`);
  }
  
  console.log('✅ fetchLeadData - Dados recebidos:', data);
  
  // Formatar resposta para manter compatibilidade com o código existente
  // Extrair o nome do vendedor do objeto aninhado
  const formattedData = {
    ...data,
    responsavel: data.vendedor?.nome || 'Não atribuído',
    parceiro_pai_id: data.parceiro?.parceiro_cadastrado_por || null
  };
  
  console.log('✅ fetchLeadData - Dados formatados:', formattedData);
  return [formattedData]; // Retornar como array para manter compatibilidade
};

// Função específica para buscar proposta por lead_id e dimensionamento_id
export const fetchPropostaData = async (leadId: string, dimensionamentoId?: string | any) => {
  try {
    // Validar e limpar o dimensionamentoId - corrigir problema do SyntheticBaseEvent
    let validDimensionamentoId: string | undefined;
    if (dimensionamentoId) {
      if (typeof dimensionamentoId === 'string') {
        validDimensionamentoId = dimensionamentoId;
      } else if (dimensionamentoId.target) {
        // Se for um evento, pegar o valor do target
        validDimensionamentoId = dimensionamentoId.target.value;
      } else if (dimensionamentoId.toString && typeof dimensionamentoId.toString === 'function') {
        validDimensionamentoId = dimensionamentoId.toString();
      }
    }
    
    console.log('🔍 fetchPropostaData - Iniciando busca para:', { leadId, dimensionamentoId, validDimensionamentoId, tipoDimensionamentoId: typeof dimensionamentoId });
    
    // CORREÇÃO CRÍTICA: Obter token de autenticação do usuário
    const { supabase } = await import('@/integrations/supabase/client');
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    
    if (!token) {
      console.error('❌ fetchPropostaData - Token de autenticação não encontrado');
      throw new Error('Token de autenticação necessário');
    }
    
    console.log('🔐 fetchPropostaData - Token obtido:', token ? `${token.substring(0, 20)}...` : 'none');
    
    // CORREÇÃO: Obter anonKey diretamente das variáveis de ambiente com fallback
    const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || 
                    SUPABASE_CONFIG.anonKey || 
                    'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImVzYWt1YnVmc2Z5a2hyeGNiZndwIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTIxNTc4OTIsImV4cCI6MjA2NzczMzg5Mn0.JPl2DmJzl-auvjzos1cIqlgTVjUE6fQeHe9i--eN3DM';
    
    if (!anonKey || anonKey.trim() === '') {
      console.error('❌ fetchPropostaData - anonKey não encontrada');
      throw new Error('API key do Supabase não configurada');
    }
    
    const headers = {
      'apikey': anonKey, // ✅ CORRETO: Usando anonKey validada
      'Authorization': `Bearer ${token}`, // ✅ CORRETO: Usando token do usuário
      'Content-Type': 'application/json',
    };

    // Estratégia: Buscar sem filtro de dimensionamento_id e filtrar no cliente
    // Isso evita o erro 400 que ocorre com a API REST do Supabase
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || SUPABASE_CONFIG.url || 'https://esakubufsfykhrxcbfwp.supabase.co';
    let url = `${supabaseUrl}/rest/v1/propostas_clientes?lead_id=eq.${leadId}`;
    // CORREÇÃO CRÍTICA: Adicionar campos calculados para verificar se dados foram salvos
    url += `&select=valor_total_kits,potencia_total_paineis,total_paineis,potencia_total_inversores,kits_r_wp,incluir_custos_estrutura_fundacao,valor_estrutura_metallica,valor_fundacao,valor_equipamentos_alugados,custo_materiais,comissao_vendedor,comissao_parceiro,comissao_parceiro_pai,comissao_vendedor_valor,comissao_parceiro_valor,comissao_parceiro_pai_valor,integracao_r_wp,impostos_percentual,mao_obra_por_placa,dimensionamento_id,status_revisao,campos_admin_preenchidos,preco_projeto,preco_integracao,custo_total_implantacao,custo_total_integracao,lucro_liquido,lucro_liquido_percentual,dimensionamento_finalizado`;
    
    console.log('🔍 fetchPropostaData - URL construída:', url);
    console.log('🔍 fetchPropostaData - Fazendo requisição...');
    
    const response = await fetch(url, { headers });
    console.log('🔍 fetchPropostaData - Status da resposta:', response.status, response.statusText);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Erro na busca da proposta:', response.status, response.statusText, errorText);
      throw new Error(`Erro ${response.status}: ${errorText}`);
    }

    const data = await response.json();
    console.log('✅ fetchPropostaData - Dados brutos recebidos:', data);
    
    if (data && data.length > 0) {
      // Se dimensionamentoId foi fornecido, filtrar pelos dados do dimensionamento específico
      let filteredData = data;
    if (validDimensionamentoId) {
      console.log('🔍 fetchPropostaData - Filtrando dados. dimensionamentoId esperado:', validDimensionamentoId);
      console.log('🔍 fetchPropostaData - Dados disponíveis para filtrar:', data.map(item => ({ 
        dimensionamento_id: item.dimensionamento_id, 
        valor_total_kits: item.valor_total_kits,
        total_paineis: item.total_paineis,
        tipo: typeof item.dimensionamento_id,
        match: item.dimensionamento_id === validDimensionamentoId 
      })));
      
      filteredData = data.filter(item => {
        const match = item.dimensionamento_id === validDimensionamentoId;
        console.log('🔍 Comparando item:', item.dimensionamento_id, '===', validDimensionamentoId, ':', match);
        return match;
      });
      console.log('🔍 fetchPropostaData - Dados filtrados pelo dimensionamento_id:', filteredData);
      
      if (filteredData.length === 0) {
        console.log('⚠️ fetchPropostaData - Nenhum dado encontrado para o dimensionamento específico');
        console.log('🔍 fetchPropostaData - Todos os dados disponíveis:', data);
        
        // CRÍTICO: Se não encontrou dados para o dimensionamento específico, 
        // isso é um erro grave que precisa ser corrigido
        throw new Error(`Nenhum dado encontrado para dimensionamento_id: ${validDimensionamentoId}`);
      }
      } else {
        console.log('⚠️ fetchPropostaData - Nenhum dimensionamentoId fornecido, usando primeiro resultado');
        filteredData = data.length > 0 ? [data[0]] : [];
      }
      
      // Mapear kits_r_wp para r_wp para manter compatibilidade com a interface
      // CRÍTICO: Sempre incluir dimensionamento_id nos dados processados
      const processedData = filteredData.map(item => ({
        ...item,
        r_wp: item.kits_r_wp || 0,
        dimensionamento_id: item.dimensionamento_id // Garantir que o campo seja preservado
      }));
      
      console.log('✅ fetchPropostaData - Dados processados:', processedData);
      return processedData;
    } else {
      console.log('⚠️ fetchPropostaData - Nenhum dado encontrado, retornando dados padrão');
      return [{
        valor_total_kits: 0,
        potencia_total_paineis: 0,
        total_paineis: 0,
        potencia_total_inversores: 0,
        r_wp: 0
      }];
    }
  } catch (error) {
    console.error('❌ Erro ao buscar dados da proposta:', error);
    // Retornar dados padrão em caso de erro
    return [{
      valor_total_kits: 0,
      potencia_total_paineis: 0,
      total_paineis: 0,
      potencia_total_inversores: 0,
      r_wp: 0
    }];
  }
};

// Função para salvar dados de precificação na tabela propostas_clientes
export const savePrecificacaoData = async (
  leadId: string, 
  precificacaoData: PrecificacaoData, 
  calculosOutput: CalculosOutput,
  dimensionamentoId?: string
) => {
  console.log('💾 savePrecificacaoData - Iniciando salvamento para:', { leadId, dimensionamentoId });
  
  // CRÍTICO: Sempre exigir dimensionamentoId para evitar sobrescrever todos os registros
  if (!dimensionamentoId || dimensionamentoId === 'undefined' || dimensionamentoId === '[object Object]') {
    console.error('❌ savePrecificacaoData - dimensionamentoId inválido:', dimensionamentoId);
    throw new Error('dimensionamentoId é obrigatório para salvar dados de precificação');
  }
  
  // CORREÇÃO CRÍTICA: Obter token de autenticação do usuário
  const { supabase } = await import('@/integrations/supabase/client');
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  
  if (!token) {
    console.error('❌ savePrecificacaoData - Token de autenticação não encontrado');
    throw new Error('Token de autenticação necessário');
  }
  
  console.log('🔐 savePrecificacaoData - Token obtido:', token ? `${token.substring(0, 20)}...` : 'none');
  
  // CORREÇÃO: Obter anonKey diretamente das variáveis de ambiente com fallback
  // Isso garante que funcione mesmo se SUPABASE_CONFIG.anonKey estiver vazia em produção
  const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || 
                  SUPABASE_CONFIG.anonKey || 
                  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImVzYWt1YnVmc2Z5a2hyeGNiZndwIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTIxNTc4OTIsImV4cCI6MjA2NzczMzg5Mn0.JPl2DmJzl-auvjzos1cIqlgTVjUE6fQeHe9i--eN3DM';
  
  if (!anonKey || anonKey.trim() === '') {
    console.error('❌ savePrecificacaoData - anonKey não encontrada');
    throw new Error('API key do Supabase não configurada');
  }
  
  console.log('🔑 savePrecificacaoData - anonKey obtida:', anonKey ? `${anonKey.substring(0, 20)}...` : 'none');
  
  // Usar UPDATE direto com token de autenticação do usuário
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || SUPABASE_CONFIG.url || 'https://esakubufsfykhrxcbfwp.supabase.co';
  const url = `${supabaseUrl}/rest/v1/propostas_clientes?lead_id=eq.${leadId}&dimensionamento_id=eq.${dimensionamentoId}`;
  console.log('💾 savePrecificacaoData - URL com filtros obrigatórios:', url);
  
  const headers = {
    'apikey': anonKey, // ✅ CORRETO: Usando anonKey validada
    'Authorization': `Bearer ${token}`, // ✅ CORRETO: Usando token do usuário
    'Content-Type': 'application/json',
    'Prefer': 'return=minimal'
  };

  const updateData = {
    incluir_custos_estrutura_fundacao: precificacaoData.incluir_custos_estrutura_fundacao,
    valor_estrutura_metallica: precificacaoData.valor_estrutura_metallica,
    valor_fundacao: precificacaoData.valor_fundacao,
    valor_equipamentos_alugados: precificacaoData.valor_equipamentos_alugados,
    custo_materiais: precificacaoData.custo_materiais,
    comissao_vendedor: precificacaoData.comissao_vendedor,
    comissao_parceiro: precificacaoData.comissao_parceiro,
    comissao_parceiro_pai: precificacaoData.comissao_parceiro_pai,
    comissao_vendedor_valor: calculosOutput.comissao_vendedor,
    comissao_parceiro_valor: calculosOutput.comissao_parceiro,
    comissao_parceiro_pai_valor: calculosOutput.comissao_parceiro_pai,
    integracao_r_wp: precificacaoData.integracao_r_wp,
    impostos_percentual: precificacaoData.impostos_percentual,
    mao_obra_por_placa: precificacaoData.mao_obra_por_placa,
    preco_projeto: calculosOutput.preco_projeto,
    preco_integracao: calculosOutput.preco_integracao,
    preco_estrutura_metallica_calc: calculosOutput.preco_estrutura_metallica,
    impostos: calculosOutput.impostos,
    mao_obra: calculosOutput.mao_obra,
    custo_total_implantacao: calculosOutput.custo_total_implantacao,
    custo_total_integracao: calculosOutput.custo_total_integracao,
    preco_por_wp_final: calculosOutput.preco_por_wp,
    lucro_liquido: calculosOutput.lucro_liquido,
    lucro_liquido_percentual: calculosOutput.lucro_liquido_percentual,
    updated_at: new Date().toISOString()
  };
  
  console.log('💾 savePrecificacaoData - URL:', url);
  console.log('💾 savePrecificacaoData - updateData:', updateData);

  const response = await fetch(url, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(updateData)
  });
  
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Erro ao salvar dados de precificação: ${response.status} ${response.statusText} - ${errorText}`);
  }

  console.log('✅ savePrecificacaoData - Dados salvos com sucesso');
  return { success: true };
};
